import React, { useState, useEffect, useMemo } from 'react';
import { 
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, 
    Button, Table, Text, Tbody, Tr, Td, Box, List, ListItem, Divider, useBreakpointValue, useToast, HStack, IconButton, Link, Image, SimpleGrid, Icon, Spinner, Tooltip, Badge as NotificationBadge, Tabs, TabList, TabPanels, Tab, TabPanel, VStack, Avatar, useDisclosure, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, FormControl, FormLabel, Input, Select
} from '@chakra-ui/react';
import { ExternalLinkIcon, ViewIcon, ViewOffIcon, AtSignIcon, EmailIcon, DeleteIcon, EditIcon, PhoneIcon } from '@chakra-ui/icons';
import { CheckIcon, WarningIcon, BellIcon, AttachmentIcon, RepeatIcon, DownloadIcon } from '@chakra-ui/icons';
import { BiRefresh } from 'react-icons/bi';
import { FaUser } from 'react-icons/fa';
import { FiUser, FiMail, FiExternalLink, FiPhone, FiEdit3 } from 'react-icons/fi';
import { FaReceipt } from 'react-icons/fa';
import { modifyOrder, getOrderDetails, sendReminder, getOrderNotes, updateCustomer, getProducts, deleteOrder, generateSimpleVoucher } from '../../services/woo';
import { resyncSingleOrder, triggerStatusChange } from '../../services/billing';
import axios from 'axios';
import FileUpload from 'components/FileUpload';
import { formatCurrency, useModuleCheck, MODULES, getSiteConfig } from 'utils/site';
import { useForm } from 'react-hook-form';
import { format } from 'rut.js';

const ModalOrderEdit = ({ order, isOpen, onClose, action, customer, onUpdate }) => {
    const [updatedOrder, setUpdatedOrder] = useState(order);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [orderNotes, setOrderNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingStatusChange, setLoadingStatusChange] = useState(false);
    const [showAllMetadata, setShowAllMetadata] = useState(false);
    const [billmysalesPdfData, setBillmysalesPdfData] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [loadingRefresh, setLoadingRefresh] = useState(false);
    const [loadingVoucher, setLoadingVoucher] = useState(false);
    const toast = useToast();
    
    // Estados para edición de usuario
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [products, setProducts] = useState([]);
    
    // Estados para confirmaciones
    const { isOpen: isEmailOpen, onOpen: onEmailOpen, onClose: onEmailClose } = useDisclosure();
    const { isOpen: isPhoneOpen, onOpen: onPhoneOpen, onClose: onPhoneClose } = useDisclosure();
    const cancelRef = React.useRef();
    
    // Form para edición
    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

    const moduleStatus = useMemo(() => ({
        [MODULES.PAYMENTS]: useModuleCheck(MODULES.PAYMENTS),
        [MODULES.FILES]: useModuleCheck(MODULES.FILES),
      }), []);

    // Breakpoint para cambiar la distribución en dispositivos móviles
    const direction = useBreakpointValue({ base: 'column', md: 'row' });

    // Colores Datofy V1 — cream/coral/ink
    const bgColor = 'white';
    const textColor = 'ink.900';
    const mutedTextColor = 'ink.400';
    const borderColor = 'cream.line';

    // Colores específicos para las tarjetas
    const cardBg = 'white';
    const cardBorder = 'cream.line';
    const shadowColor = 'rgba(43,39,35,0.06)';

    const selectedMetadataKeys = [
        'mes', 
        'concepto', 
        'monto', 
        'fecha_vencimiento', 
        'order_count_reminder',
        'billing_rut',
        'transactionDate',
        'paymentType',
        'billmysales_order_id',
    ];

    // Función para truncar valores largos
    const truncateValue = (value, maxLength = 100) => {
        if (typeof value === 'object') {
            const jsonString = JSON.stringify(value, null, 2);
            if (jsonString.length > maxLength) {
                return jsonString.substring(0, maxLength) + '...';
            }
            return jsonString;
        }
        
        const stringValue = String(value);
        if (stringValue.length > maxLength) {
            return stringValue.substring(0, maxLength) + '...';
        }
        return stringValue;
    };

    // Función para navegar al perfil en la misma ventana
    const navigateToProfile = (userId) => {
        if (userId) {
            const profileUrl = `/#/admin/usuario/${userId}`;
            window.location.href = profileUrl;
        }
    };

    // Función para navegar al perfil en nueva ventana
    const navigateToProfileNewWindow = (userId) => {
        if (userId) {
            const profileUrl = `/#/admin/usuario/${userId}`;
            window.open(profileUrl, '_blank');
        }
    };

    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (isOpen) {
                setLoading(true);
                try {
                    const response = await getOrderDetails(order.id);
                    setUpdatedOrder(response.data);
                    const notes = await getOrderNotes(order.id);
                    setOrderNotes(notes);
                    
                    // Cargar grupos y productos para edición
                    const config = JSON.parse(getSiteConfig());
                    setGroups(config?.groups || []);
                    
                    const allProducts = await getProducts();
                    setProducts(allProducts || []);
                } catch (error) {
                    console.error('Error fetching updated order details:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        if (isOpen) {
            fetchOrderDetails();
        }
    }, [isOpen, order.id]);

    useEffect(() => {
        const fetchUploadedFiles = () => {
            // Buscar en los metadatos de la orden si hay archivos subidos y convertirlo en un array
            const filesMeta = updatedOrder.meta_data?.find(item => item.key === 'uploaded_file_url');
            if (filesMeta && Array.isArray(filesMeta.value)) {
                setUploadedFiles(filesMeta.value);
            } else {
                setUploadedFiles([]);
            }
        };
        fetchUploadedFiles();
    }, [updatedOrder]);

    const refreshOrderDetails = async () => {
        setLoading(true);
        try {
            const response = await getOrderDetails(order.id);
            setUpdatedOrder(response.data);
            const notes = await getOrderNotes(order.id);
            setOrderNotes(notes);
            toast({
                title: 'Pedido Actualizado',
                description: `El pedido #${order.id} se ha actualizado con éxito.`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error fetching updated order details:', error);
            toast({
                title: 'Error',
                description: 'No se pudo actualizar el estado del pedido. Inténtalo nuevamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Función para generar comprobante simple
    const handleGenerateVoucher = async () => {
        setLoadingVoucher(true);
        
        try {
            const result = await generateSimpleVoucher(updatedOrder.id);
            
            if (result.success) {
                toast({
                    title: 'Comprobante Generado',
                    description: `Comprobante #${updatedOrder.id} generado exitosamente`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Error al Generar Comprobante',
                    description: result.message || 'No se pudo generar el comprobante',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error generando comprobante simple:', error);
            toast({
                title: 'Error',
                description: 'Ocurrió un error inesperado al generar el comprobante.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoadingVoucher(false);
        }
    };

    const handleOrderStatusChange = async (newStatus, successMessage, errorMessage) => {
        const confirmed = window.confirm(`¿Estás seguro de que deseas marcar este pedido como ${newStatus}?`);
        if (!confirmed) return;

        setLoadingStatusChange(true);
        try {
            await modifyOrder(order.id, [], { status: newStatus });
            await refreshOrderDetails();
            // Llamar callback de actualización si existe
            if (onUpdate && typeof onUpdate === 'function') {
                onUpdate();
            } else if (action && typeof action === 'function') {
                action();
            }
        } catch (error) {
            console.error(`Error al cambiar el estado del pedido a ${newStatus}:`, error);
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingStatusChange(false);
        }
    };

    const handleOrderDelete = async () => {
        const confirmed = window.confirm('¿Estás seguro de que deseas ELIMINAR permanentemente este pedido? Esta acción no se puede deshacer.');
        if (!confirmed) return;

        setLoadingStatusChange(true);
        try {
            await deleteOrder(order.id);
            toast({
                title: 'Pedido Eliminado',
                description: `El pedido #${order.id} ha sido eliminado permanentemente.`,
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            onClose(); // Cerrar el modal después de eliminar
            // Llamar callback de actualización si existe
            if (onUpdate && typeof onUpdate === 'function') {
                onUpdate();
            } else if (action && typeof action === 'function') {
                action();
            }
        } catch (error) {
            console.error('Error al eliminar el pedido:', error);
            toast({
                title: 'Error',
                description: 'No se pudo eliminar el pedido. Inténtalo nuevamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingStatusChange(false);
        }
    };

    const handleReSyncOrder = async () => {
        const currentStatus = updatedOrder.status;
        const confirmed = window.confirm(
            `¿Deseas re-sincronizar este pedido?\n\n` +
            `Esto re-sincronizará el pedido, actualizando la información del alumno/apoderado ` +
            `y regenerando la glosa en los items.\n\n` +
            `Estado actual: ${getOrderStatusLabel(currentStatus).label}`
        );
        if (!confirmed) return;

        setLoadingStatusChange(true);
        try {
            toast({
                title: 'Iniciando re-sincronización',
                description: `Cambiando estado de orden #${order.id}...`,
                status: 'info',
                duration: 2000,
                isClosable: true,
            });

            // Usar endpoint que dispara hooks nativos de WooCommerce
            // Equivalente a "Acciones por lote > Cambiar estado" en el admin de WordPress
            const result = await triggerStatusChange(order.id, currentStatus);

            if (result.success) {
                toast({
                    title: 'Re-sincronización completada',
                    description: `El pedido #${order.id} ha sido actualizado exitosamente.`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });

                // Esperar 3 segundos para que se procesen todos los flujos de vida
                await new Promise(resolve => setTimeout(resolve, 3000));

                // Recargar detalles de la orden
                await refreshOrderDetails();

                // Llamar callback de actualización si existe
                if (onUpdate && typeof onUpdate === 'function') {
                    onUpdate();
                } else if (action && typeof action === 'function') {
                    action();
                }
            } else {
                toast({
                    title: 'Error en re-sincronización',
                    description: result.message || 'No se pudo re-sincronizar el pedido.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error al re-sincronizar el pedido:', error);
            toast({
                title: 'Error',
                description: 'No se pudo re-sincronizar el pedido. Inténtalo nuevamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingStatusChange(false);
        }
    };

    const handleOrderSendNotification = async () => {
        const confirmed = window.confirm('¿Estás seguro de que deseas enviar un recordatorio para este pedido?');
        if (!confirmed) return;

        setLoading(true);
        try {
            const orderDetails = await getOrderDetails(order.id);
            const orderCountReminder = orderDetails.data.meta_data && orderDetails.data.meta_data.find(item => item.key === 'order_count_reminder');
            if (orderCountReminder) {
                const updatedValue = parseInt(orderCountReminder.value) + 1;
                await sendReminder(order.id);
                await modifyOrder(order.id, [{ key: 'order_count_reminder', value: updatedValue }]);
            } else {
                await sendReminder(order.id);
                await modifyOrder(order.id, [{ key: 'order_count_reminder', value: 1 }]);
            }
            await refreshOrderDetails();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilesChange = async (files) => {
        setUploadedFiles(files || []);
        await refreshOrderDetails();
    };

    // Función para abrir modal de edición
    const handleEditCustomer = () => {
        const customerInfo = updatedOrder?.customer_info;
        if (customerInfo) {
            setEditModalOpen(true);
            
            // Llenar el formulario con los datos del cliente
            setValue('first_name', customerInfo.first_name || '');
            setValue('last_name', customerInfo.last_name || '');
            setValue('email', customerInfo.email || '');
            setValue('username', customerInfo.username || '');
            
            // Obtener el grupo actual del cliente
            const currentGroup = customerInfo.meta_data?.find(m => m.key === 'group_name')?.value || '';
            setValue('group', currentGroup);
            
            // Obtener la suscripción actual del cliente
            const currentSubscription = customerInfo.meta_data?.find(m => m.key === 'suscription_name')?.value || '';
            setValue('suscription', currentSubscription);
        }
    };

    // Función para actualizar usuario
    const handleUpdateCustomer = async (data) => {
        setEditLoading(true);
        try {
            const updateData = {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                username: data.username,
                meta_data: [
                    { key: 'group_name', value: data.group || '' },
                    ...(data.suscription ? [{ key: 'suscription_name', value: data.suscription }] : [])
                ]
            };

            const result = await updateCustomer(updatedOrder.customer_info.id, updateData);
            
            if (result.success) {
                toast({
                    title: "Usuario actualizado",
                    description: "Los datos del usuario han sido actualizados exitosamente.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                setEditModalOpen(false);
                reset();
                await refreshOrderDetails(); // Refrescar datos del modal
            } else {
                toast({
                    title: "Error al actualizar",
                    description: result.message || "No se pudo actualizar el usuario.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            toast({
                title: "Error",
                description: "Ocurrió un error al actualizar el usuario.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setEditLoading(false);
        }
    };

    // Función para cancelar edición
    const handleCancelEdit = () => {
        setEditModalOpen(false);
        reset();
    };

    // Función para confirmar envío de email
    const handleConfirmEmail = () => {
        window.open(`mailto:${updatedOrder?.customer_info?.email}`);
        onEmailClose();
    };

    // Función para confirmar llamada
    const handleConfirmPhone = () => {
        const phoneNumber = updatedOrder?.customer_info?.meta_data?.find(m => m.key === 'telefono')?.value ||
                           updatedOrder?.customer_info?.meta_data?.find(m => m.key === 'apoderado_telefono')?.value;
        if (phoneNumber) {
            window.open(`tel:${phoneNumber}`);
        }
        onPhoneClose();
    };

    // Función para obtener datos del PDF de BillMySales
    const handleGetBillMySalesPdf = async () => {
        setLoadingPdf(true);
        try {
            const config = JSON.parse(getSiteConfig());
            const wpApiUrl = config?.configs?.find(c => c.key === 'WP_API_URL')?.value;
            
            if (!wpApiUrl) {
                throw new Error('WP_API_URL no configurado');
            }

            const response = await axios.get(`${wpApiUrl}/datofy/v1/orders/${order.id}/billmysales-pdf`);
            
            if (response.data.success) {
                setBillmysalesPdfData(response.data);
                
                // Si hay link PDF, abrirlo automáticamente
                if (response.data.links?.pdf) {
                    window.open(response.data.links.pdf, '_blank');
                }
                
                toast({
                    title: 'PDF Obtenido',
                    description: `Factura #${response.data.invoice_data?.number || 'N/A'} - ${response.data.invoice_data?.document || 'Documento'}`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Sin BillMySales ID',
                    description: response.data.message || 'Esta orden no tiene BillMySales Order ID asociado',
                    status: 'warning',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error obteniendo PDF de BillMySales:', error);
            toast({
                title: 'Error',
                description: 'No se pudo obtener el PDF. Verifique que la orden tenga BillMySales ID.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingPdf(false);
        }
    };

    // Función para actualizar datos de la orden (solo fetch)
    const handleRefreshOrderData = async () => {
        setLoadingRefresh(true);
        try {
            // Fetch para obtener datos actualizados de la orden
            const response = await getOrderDetails(order.id);
            setUpdatedOrder(response.data);
            const notes = await getOrderNotes(order.id);
            setOrderNotes(notes);
            
            // Limpiar datos de PDF para recargar si es necesario
            setBillmysalesPdfData(null);
            
            toast({
                title: 'Actualizado',
                status: 'success',
                duration: 1500,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error actualizando datos:', error);
            toast({
                title: 'Error al actualizar',
                status: 'error',
                duration: 2000,
                isClosable: true,
            });
        } finally {
            setLoadingRefresh(false);
        }
    };

    // TODO: Ya existe una función similar en src/utils/site.js, considerar reutilizarla
    const getOrderStatusLabel = (status) => {
        switch (status) {
            case 'pending':
                return { label: 'Pendiente', bg: 'warn.bg', color: 'warn.500' };
            case 'processing':
                return { label: 'En Proceso', bg: 'sky.bg', color: 'sky.ink' };
            case 'on-hold':
                return { label: 'En Espera', bg: 'butter.bg', color: 'butter.ink' };
            case 'completed':
                return { label: 'Pagado', bg: 'ok.bg', color: 'ok.500' };
            case 'cancelled':
                return { label: 'Cancelado', bg: 'danger.bg', color: 'danger.500' };
            case 'refunded':
                return { label: 'Reembolsado', bg: 'lav.bg', color: 'lav.ink' };
            case 'failed':
                return { label: 'Fallido', bg: 'danger.bg', color: 'danger.500' };
            case 'used':
                return { label: 'Escaneado', bg: 'sky.bg', color: 'sky.ink' };
            case 'repactado':
                return { label: 'Repactado', bg: 'teal.bg', color: 'teal.ink' };
            default:
                return { label: 'Desconocido', bg: 'cream.bg2', color: 'ink.400' };
        }
    };

    const { label: statusLabel, bg: statusBg, color: statusColor } = getOrderStatusLabel(updatedOrder.status);
    const filteredMetadata = updatedOrder.meta_data?.filter(item => selectedMetadataKeys.includes(item.key)) || [];

    const orderCountReminder = updatedOrder.meta_data?.find(item => item.key === 'order_count_reminder')?.value || 0;

    // Layout similar al profile
    const UserSidebar = () => (
        <VStack spacing={0} align="stretch" w="240px" flexShrink={0}>
            <Box bg={cardBg} border="1px solid" borderColor="cream.line" borderRadius="16px" p={6} boxShadow={`0 4px 6px -1px ${shadowColor}`}>
                <VStack spacing={4}>
                    {/* Avatar y nombre */}
                    <VStack spacing={3}>
                        <Avatar
                            size="xl"
                            name={`${updatedOrder?.customer_info?.first_name} ${updatedOrder?.customer_info?.last_name}`}
                            bg="coral.soft"
                            color="coral.700"
                        />
                        <VStack spacing={1} textAlign="center">
                            <Text fontSize="lg" fontWeight="800" color={textColor}>
                                {updatedOrder?.customer_info?.first_name} {updatedOrder?.customer_info?.last_name}
                            </Text>
                            <Box as="span" px="10px" py="3px" borderRadius="999px" bg={statusBg} color={statusColor} fontSize="11px" fontWeight="700">
                                {statusLabel}
                            </Box>
                        </VStack>
                    </VStack>

                    <Divider borderColor={cardBorder} />

                    {/* Información básica */}
                    <VStack spacing={3} align="stretch" w="full">
                        <Text fontSize="xs" fontWeight="800" color={mutedTextColor} textTransform="uppercase">
                            Información del Usuario
                        </Text>

                        <HStack spacing={3}>
                            <Icon as={AtSignIcon} color={mutedTextColor} boxSize={3} />
                            <Text fontSize="xs" color={mutedTextColor}>RUT:</Text>
                            <Text fontSize="xs" fontWeight="600" color={textColor}>
                                {updatedOrder?.customer_info?.username}
                            </Text>
                        </HStack>

                        <HStack spacing={3}>
                            <Icon as={FiMail} color={mutedTextColor} boxSize={3} />
                            <Text fontSize="xs" color={mutedTextColor}>Email:</Text>
                            <Text fontSize="xs" fontWeight="600" color={textColor} isTruncated>
                                {updatedOrder?.customer_info?.meta_data?.find(m => m.key === 'apoderado_email')?.value ||
                                 updatedOrder?.customer_info?.email}
                            </Text>
                        </HStack>

                        <HStack spacing={3}>
                            <Icon as={FiUser} color={mutedTextColor} boxSize={3} />
                            <Text fontSize="xs" color={mutedTextColor}>ID:</Text>
                            <Text fontSize="xs" fontWeight="600" color={textColor}>
                                #{updatedOrder?.customer_info?.id}
                            </Text>
                        </HStack>
                    </VStack>

                    <Divider borderColor={cardBorder} />

                    {/* Botones de acción */}
                    <VStack spacing={2} w="full">
                        <Button
                            leftIcon={<Icon as={FiUser} />}
                            bg="cream.bg2"
                            color="ink.700"
                            borderRadius="10px"
                            _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                            size="sm"
                            w="full"
                            onClick={() => navigateToProfileNewWindow(updatedOrder?.customer_info?.id)}
                            isDisabled={!updatedOrder?.customer_info?.id}
                        >
                            Ver Perfil
                        </Button>

                        <Button
                            leftIcon={<Icon as={FiEdit3} />}
                            bg="coral.500"
                            color="white"
                            fontWeight="600"
                            borderRadius="10px"
                            _hover={{ bg: 'coral.600' }}
                            _active={{ bg: 'coral.700' }}
                            size="sm"
                            w="full"
                            onClick={handleEditCustomer}
                        >
                            Editar
                        </Button>

                        <Button
                            leftIcon={<Icon as={FiMail} />}
                            bg="cream.bg2"
                            color="ink.700"
                            borderRadius="10px"
                            _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                            size="sm"
                            w="full"
                            onClick={onEmailOpen}
                        >
                            Enviar Email
                        </Button>

                        {(updatedOrder?.customer_info?.meta_data?.find(m => m.key === 'telefono')?.value ||
                          updatedOrder?.customer_info?.meta_data?.find(m => m.key === 'apoderado_telefono')?.value) && (
                            <Button
                                leftIcon={<Icon as={FiPhone} />}
                                bg="cream.bg2"
                                color="ink.700"
                                borderRadius="10px"
                                _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                                size="sm"
                                w="full"
                                onClick={onPhoneOpen}
                            >
                                Llamar
                            </Button>
                        )}
                    </VStack>
                </VStack>
            </Box>
        </VStack>
    );

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="4xl">
                <ModalOverlay />
                <ModalContent maxH="90vh" bg="cream.bg" borderRadius="18px" fontFamily="jakarta">
                    <ModalHeader borderBottom="1px solid" borderColor={borderColor} color="ink.900" fontWeight="800">
                        <HStack spacing={3}>
                            {loading && <Spinner size="sm" color="coral.500" />}
                            <Text>Detalles del Pago #{updatedOrder.id}</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody p={6} overflowY="auto">
                        <HStack spacing={6} align="flex-start">
                            {/* Sidebar del usuario */}
                            <UserSidebar />

                            {/* Contenido principal */}
                            <VStack spacing={6} align="stretch" flex={1} maxH="70vh" overflowY="auto" className="custom-scrollbar">
                                {/* Header con estado y acciones */}
                                <Box bg={cardBg} border="1px solid" borderColor="cream.line" borderRadius="16px" p={6} boxShadow={`0 4px 6px -1px ${shadowColor}`}>
                                    <VStack spacing={4} align="stretch">
                                        <HStack justify="space-between" align="center">
                                            <VStack align="flex-start" spacing={1}>
                                                <Text fontSize="lg" fontWeight="800" color={textColor}>
                                                    Estado del Pago
                                                </Text>
                                                <Text fontSize="sm" color={mutedTextColor}>
                                                    Gestiona el estado y acciones del pago
                                                </Text>
                                            </VStack>
                                            <Box as="span" px="14px" py="6px" borderRadius="999px" bg={statusBg} color={statusColor} fontSize="13px" fontWeight="700">
                                                {statusLabel}
                                            </Box>
                                        </HStack>

                                        <Divider borderColor={cardBorder} />

                                        {/* Botones de Acción */}
                                        <HStack spacing={3} justify="space-between" w="full">
                                            <HStack spacing={3}>
                                                <Tooltip label="Re-sincronizar datos del pedido y regenerar glosa">
                                                    <IconButton
                                                        icon={loadingStatusChange ? <Spinner size="sm" /> : <RepeatIcon />}
                                                        bg="lav.bg"
                                                        color="lav.ink"
                                                        _hover={{ opacity: 0.85 }}
                                                        aria-label="Re-sincronizar Pedido"
                                                        onClick={handleReSyncOrder}
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Descargar PDF de Factura (BillMySales)">
                                                    <IconButton
                                                        icon={loadingPdf ? <Spinner size="sm" /> : <DownloadIcon />}
                                                        bg="teal.bg"
                                                        color="teal.ink"
                                                        _hover={{ opacity: 0.85 }}
                                                        aria-label="Descargar PDF"
                                                        onClick={handleGetBillMySalesPdf}
                                                        isDisabled={!updatedOrder.meta_data?.find(item => item.key === 'billmysales_order_id')?.value}
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Marcar como pagado">
                                                    <IconButton
                                                        icon={loadingStatusChange ? <Spinner size="sm" /> : <CheckIcon />}
                                                        bg="ok.bg"
                                                        color="ok.500"
                                                        _hover={{ opacity: 0.85 }}
                                                        aria-label="Marcar Completado"
                                                        isDisabled={updatedOrder.status !== 'processing' && updatedOrder.status !== 'pending'}
                                                        onClick={() => handleOrderStatusChange('completed', 'Pedido Completado', 'No se pudo completar el pedido. Inténtalo nuevamente.')}
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Cancelar Pago">
                                                    <IconButton
                                                        icon={loadingStatusChange ? <Spinner size="sm" /> : <WarningIcon />}
                                                        bg="danger.bg"
                                                        color="danger.500"
                                                        _hover={{ opacity: 0.85 }}
                                                        aria-label="Cancelar Pago"
                                                        isDisabled={updatedOrder.status === 'cancelled' || updatedOrder.status === 'completed' || updatedOrder.status === 'repactado'}
                                                        onClick={() => handleOrderStatusChange('cancelled', 'Pago Cancelado', 'No se pudo cancelar el pago. Inténtalo nuevamente.')}
                                                    />
                                                </Tooltip>
                                                <Tooltip label="Marcar como Repactado">
                                                    <IconButton
                                                        icon={loadingStatusChange ? <Spinner size="sm" /> : <Icon as={() => <span style={{fontSize:'14px', fontWeight:'bold'}}>R</span>} />}
                                                        bg="teal.bg"
                                                        color="teal.ink"
                                                        _hover={{ opacity: 0.85 }}
                                                        aria-label="Repactar"
                                                        isDisabled={updatedOrder.status === 'repactado' || updatedOrder.status === 'completed'}
                                                        onClick={() => handleOrderStatusChange('repactado', 'Orden Repactada', 'No se pudo marcar como repactado.')}
                                                    />
                                                </Tooltip>
                                                {updatedOrder.status === 'pending' && (
                                                    <Tooltip label="Enviar Recordatorio">
                                                        <Box position="relative">
                                                            <IconButton
                                                                icon={loading ? <Spinner size="sm" /> : <BellIcon />}
                                                                bg="coral.soft"
                                                                color="coral.700"
                                                                _hover={{ bg: 'coral.tint' }}
                                                                aria-label="Enviar Recordatorio"
                                                                onClick={handleOrderSendNotification}
                                                            />
                                                            {orderCountReminder > 0 && (
                                                                <NotificationBadge
                                                                    position="absolute"
                                                                    top="-1"
                                                                    right="-1"
                                                                    fontSize="xs"
                                                                    borderRadius="full"
                                                                    px={1}
                                                                    py={0.5}
                                                                    backgroundColor="danger.500"
                                                                    color="white"
                                                                    boxShadow="0 0 0 1px white"
                                                                >
                                                                    {orderCountReminder}
                                                                </NotificationBadge>
                                                            )}
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </HStack>

                                            <Tooltip label="Actualizar datos de la orden">
                                                <IconButton
                                                    icon={loadingRefresh ? <Spinner size="sm" /> : <Icon as={BiRefresh} />}
                                                    bg="cream.bg2"
                                                    color="ink.700"
                                                    _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                                                    variant="solid"
                                                    aria-label="Actualizar Datos"
                                                    onClick={handleRefreshOrderData}
                                                />
                                            </Tooltip>
                                        </HStack>
                                    </VStack>
                                </Box>

                                {/* Información de fechas */}
                                <Box bg={cardBg} border="1px solid" borderColor="cream.line" borderRadius="16px" p={6} boxShadow={`0 4px 6px -1px ${shadowColor}`}>
                                    <VStack spacing={4} align="stretch">
                                        <Text fontSize="lg" fontWeight="800" color={textColor}>
                                            Información de Fechas
                                        </Text>

                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            <Box>
                                                <Text fontSize="sm" fontWeight="700" color={mutedTextColor} mb={1}>
                                                    Fecha de Creación
                                                </Text>
                                                <Text fontSize="sm" color={textColor}>
                                                    {updatedOrder?.date_created ? 
                                                        new Date(updatedOrder.date_created).toLocaleString('es-CL', {
                                                            dateStyle: 'short',
                                                            timeStyle: 'short'
                                                        }) : 
                                                        'No disponible'
                                                    }
                                                </Text>
                                            </Box>
                                            
                                            <Box>
                                                <Text fontSize="sm" fontWeight="bold" color={mutedTextColor} mb={1}>
                                                    Última Modificación
                                                </Text>
                                                <Text fontSize="sm" color={textColor}>
                                                    {updatedOrder?.date_modified ? 
                                                        new Date(updatedOrder.date_modified).toLocaleString('es-CL', {
                                                            dateStyle: 'short',
                                                            timeStyle: 'short'
                                                        }) : 
                                                        'No disponible'
                                                    }
                                                </Text>
                                            </Box>
                                            
                                            {updatedOrder?.date_completed && (
                                                <Box>
                                                    <Text fontSize="sm" fontWeight="bold" color={mutedTextColor} mb={1}>
                                                        Fecha de Completado
                                                    </Text>
                                                    <Text fontSize="sm" color={textColor}>
                                                        {new Date(updatedOrder.date_completed).toLocaleString('es-CL', {
                                                            dateStyle: 'short',
                                                            timeStyle: 'short'
                                                        })}
                                                    </Text>
                                                </Box>
                                            )}
                                            
                                            {updatedOrder?.date_paid && (
                                                <Box>
                                                    <Text fontSize="sm" fontWeight="bold" color={mutedTextColor} mb={1}>
                                                        Fecha de Pago
                                                    </Text>
                                                    <Text fontSize="sm" color={textColor}>
                                                        {new Date(updatedOrder.date_paid).toLocaleString('es-CL', {
                                                            dateStyle: 'short',
                                                            timeStyle: 'short'
                                                        })}
                                                    </Text>
                                                </Box>
                                            )}
                                        </SimpleGrid>
                                    </VStack>
                                </Box>

                                {/* Tabs de contenido */}
                                <Box bg={cardBg} border="1px solid" borderColor="cream.line" borderRadius="16px" boxShadow={`0 4px 6px -1px ${shadowColor}`}>
                                    <Tabs colorScheme="orange">
                                        <TabList borderBottom="1px" borderColor={borderColor} px={6} pt={4}>
                                            <Tab fontSize="sm" fontWeight="600" color="ink.400" _selected={{ color: 'coral.700', borderColor: 'coral.500' }}>Información General</Tab>
                                            {moduleStatus[MODULES.FILES] && <Tab fontSize="sm" fontWeight="600" color="ink.400" _selected={{ color: 'coral.700', borderColor: 'coral.500' }}>Archivos del Pago</Tab>}
                                        </TabList>

                                        <TabPanels>
                                            <TabPanel p={6}>
                                                <VStack spacing={6} align="stretch">
                                                    {/* Items del Pedido */}
                                                    <Box>
                                                        <Text fontSize="lg" fontWeight="800" color={textColor} mb={4}>
                                                            Items del Pedido
                                                        </Text>
                                                        <Box bg="cream.bg2" borderRadius="12px" p={4}>
                                                            <Table size="sm" variant="simple">
                                                                <Tbody>
                                                                    {updatedOrder?.line_items?.map((item, index) => (
                                                                        <Tr key={index}>
                                                                            <Td fontWeight="700" color={textColor} border="none">
                                                                                {item.quantity} x {item.name}
                                                                            </Td>
                                                                            <Td textAlign="right" color={textColor} border="none">
                                                                                {formatCurrency(item.total)}
                                                                            </Td>
                                                                        </Tr>
                                                                    ))}

                                                                    {/* Impuestos */}
                                                                    {updatedOrder?.tax_lines?.length > 0 &&
                                                                        updatedOrder.tax_lines.map((item, index) => (
                                                                            <Tr key={`tax-${index}`}>
                                                                                <Td fontWeight="700" color={textColor} border="none">
                                                                                    {item.label}
                                                                                </Td>
                                                                                <Td textAlign="right" color={textColor} border="none">
                                                                                    {formatCurrency(item.tax_total)}
                                                                                </Td>
                                                                            </Tr>
                                                                        ))
                                                                    }

                                                                    {/* Total */}
                                                                    <Tr>
                                                                        <Td fontWeight="800" fontSize="lg" color={textColor} borderTop="2px solid" borderColor={borderColor}>
                                                                            Total:
                                                                        </Td>
                                                                        <Td textAlign="right" fontWeight="800" fontSize="lg" color="ok.500" borderTop="2px solid" borderColor={borderColor}>
                                                                            {formatCurrency(updatedOrder.total)}
                                                                        </Td>
                                                                    </Tr>
                                                                </Tbody>
                                                            </Table>
                                                        </Box>
                                                    </Box>

                                                    {/* Información/Metadatos */}
                                                    <Box>
                                                        <HStack justify="space-between" align="center" mb={4}>
                                                            <Text fontSize="lg" fontWeight="800" color={textColor}>
                                                                Información del Pago
                                                            </Text>
                                                            <Button
                                                                size="sm"
                                                                leftIcon={showAllMetadata ? <ViewOffIcon /> : <ViewIcon />}
                                                                onClick={() => setShowAllMetadata(!showAllMetadata)}
                                                                bg={showAllMetadata ? "danger.bg" : "cream.bg2"}
                                                                color={showAllMetadata ? "danger.500" : "ink.700"}
                                                                borderRadius="9px"
                                                                _hover={{ bg: showAllMetadata ? 'danger.bg' : 'coral.soft', color: showAllMetadata ? 'danger.500' : 'coral.700' }}
                                                            >
                                                                {showAllMetadata ? "Ocultar detalles" : "Ver todos los detalles"}
                                                            </Button>
                                                        </HStack>
                                                        <Box
                                                            bg="cream.bg2"
                                                            borderRadius="12px"
                                                            p={4}
                                                            maxH={showAllMetadata ? "300px" : "auto"}
                                                            overflowY={showAllMetadata ? "auto" : "visible"}
                                                            className={showAllMetadata ? "custom-scrollbar" : ""}
                                                        >
                                                            <Table size="sm" variant="simple">
                                                                <Tbody>
                                                                    {(showAllMetadata ? updatedOrder.meta_data : filteredMetadata)?.map((item, index) => (
                                                                        <Tr key={index}>
                                                                            <Td fontWeight="700" minW="120px" wordBreak="break-word" color={textColor} border="none">
                                                                                {item.key}:
                                                                            </Td>
                                                                            <Td 
                                                                                maxW="300px" 
                                                                                wordBreak="break-word" 
                                                                                whiteSpace="pre-wrap"
                                                                                fontSize="sm"
                                                                                color={textColor}
                                                                                border="none"
                                                                            >
                                                                                {truncateValue(item.value)}
                                                                            </Td>
                                                                        </Tr>
                                                                    ))}
                                                                </Tbody>
                                                            </Table>
                                                        </Box>
                                                    </Box>

                                                    {/* Información de BillMySales */}
                                                    {updatedOrder.meta_data?.find(item => item.key === 'billmysales_order_id')?.value && (
                                                        <Box>
                                                            <HStack justify="space-between" align="center" mb={4}>
                                                                <Text fontSize="lg" fontWeight="800" color={textColor}>
                                                                    Información de Facturación
                                                                </Text>
                                                                <Box as="span" px="12px" py="4px" borderRadius="999px" bg="teal.bg" color="teal.ink" fontSize="12px" fontWeight="700">
                                                                    BillMySales
                                                                </Box>
                                                            </HStack>
                                                            <Box
                                                                bg="teal.bg"
                                                                borderRadius="12px"
                                                                p={4}
                                                                border="1px solid"
                                                                borderColor="teal.ink"
                                                            >
                                                                <VStack spacing={3} align="stretch">
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" fontWeight="700" color={mutedTextColor}>
                                                                            BillMySales Order ID:
                                                                        </Text>
                                                                        <Text fontSize="sm" fontWeight="700" color="teal.ink">
                                                                            #{updatedOrder.meta_data?.find(item => item.key === 'billmysales_order_id')?.value}
                                                                        </Text>
                                                                    </HStack>

                                                                    {billmysalesPdfData && (
                                                                        <>
                                                                            <Divider borderColor="teal.ink" />

                                                                            {billmysalesPdfData.invoice_data && (
                                                                                <VStack spacing={2} align="stretch">
                                                                                    <HStack justify="space-between">
                                                                                        <Text fontSize="sm" color={mutedTextColor}>Documento:</Text>
                                                                                        <Text fontSize="sm" fontWeight="600" color={textColor}>
                                                                                            {billmysalesPdfData.invoice_data.document || 'N/A'}
                                                                                        </Text>
                                                                                    </HStack>

                                                                                    <HStack justify="space-between">
                                                                                        <Text fontSize="sm" color={mutedTextColor}>Número:</Text>
                                                                                        <Text fontSize="sm" fontWeight="600" color={textColor}>
                                                                                            #{billmysalesPdfData.invoice_data.number || 'N/A'}
                                                                                        </Text>
                                                                                    </HStack>

                                                                                    <HStack justify="space-between">
                                                                                        <Text fontSize="sm" color={mutedTextColor}>Fecha:</Text>
                                                                                        <Text fontSize="sm" fontWeight="600" color={textColor}>
                                                                                            {billmysalesPdfData.invoice_data.date || 'N/A'}
                                                                                        </Text>
                                                                                    </HStack>

                                                                                    <HStack justify="space-between">
                                                                                        <Text fontSize="sm" color={mutedTextColor}>Total:</Text>
                                                                                        <Text fontSize="sm" fontWeight="700" color="ok.500">
                                                                                            {billmysalesPdfData.invoice_data.currency?.symbol || '$'}{billmysalesPdfData.invoice_data.total?.toLocaleString() || 'N/A'}
                                                                                        </Text>
                                                                                    </HStack>

                                                                                    {billmysalesPdfData.billing_status && (
                                                                                        <HStack justify="space-between">
                                                                                            <Text fontSize="sm" color={mutedTextColor}>Estado:</Text>
                                                                                            <Box as="span" px="8px" py="2px" borderRadius="999px" bg="ok.bg" color="ok.500" fontSize="10.5px" fontWeight="700">
                                                                                                {billmysalesPdfData.billing_status.name}
                                                                                            </Box>
                                                                                        </HStack>
                                                                                    )}
                                                                                </VStack>
                                                                            )}

                                                                            {billmysalesPdfData.links?.pdf && (
                                                                                <>
                                                                                    <Divider borderColor="teal.ink" />
                                                                                    <Button
                                                                                        leftIcon={<ExternalLinkIcon />}
                                                                                        bg="teal.ink"
                                                                                        color="white"
                                                                                        borderRadius="9px"
                                                                                        _hover={{ opacity: 0.9 }}
                                                                                        size="sm"
                                                                                        onClick={() => window.open(billmysalesPdfData.links.pdf, '_blank')}
                                                                                    >
                                                                                        Abrir PDF en Nueva Ventana
                                                                                    </Button>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </VStack>
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Notas del Pedido */}
                                                    <Box>
                                                        <Text fontSize="lg" fontWeight="800" color={textColor} mb={4}>
                                                            Notas del Pago
                                                        </Text>
                                                        {loading ? (
                                                            <Box display="flex" alignItems="center" justifyContent="center" h="50px">
                                                                <Spinner size="sm" color="coral.500" />
                                                            </Box>
                                                        ) : orderNotes.length > 0 ? (
                                                            <Box
                                                                bg="cream.bg2"
                                                                borderRadius="12px"
                                                                p={4}
                                                                maxH="200px"
                                                                overflowY="auto"
                                                                className="custom-scrollbar"
                                                            >
                                                                <List spacing={3}>
                                                                    {orderNotes.map((note, index) => (
                                                                        <ListItem key={index}>
                                                                            <HStack spacing={3} align="flex-start">
                                                                                <Box as="span" px="8px" py="2px" borderRadius="999px" bg="lav.bg" color="lav.ink" fontSize="10.5px" fontWeight="700" flexShrink={0}>
                                                                                    {note.date_created}
                                                                                </Box>
                                                                                <Text fontSize="sm" color={textColor} flex={1}>
                                                                                    <Box as="span" dangerouslySetInnerHTML={{ __html: note.note }} />
                                                                                </Text>
                                                                            </HStack>
                                                                        </ListItem>
                                                                    ))}
                                                                </List>
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                bg="cream.bg2"
                                                                borderRadius="12px"
                                                                p={6}
                                                                textAlign="center"
                                                            >
                                                                <Text color={mutedTextColor}>No hay notas disponibles.</Text>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </VStack>
                                            </TabPanel>

                                            {/* Pestaña de Archivos */}
                                            {moduleStatus[MODULES.FILES] && (
                                                <TabPanel p={6}>
                                                    <VStack spacing={6} align="stretch">
                                                        <FileUpload orderId={order.id} onSuccess={handleFilesChange} />

                                                        {/* Imágenes Subidas */}
                                                        <Box>
                                                            <Text fontSize="lg" fontWeight="800" color={textColor} mb={4}>
                                                                Imágenes Subidas
                                                            </Text>
                                                            {uploadedFiles?.filter(file => file.match(/\.(jpeg|jpg|gif|png)$/)).length > 0 ? (
                                                                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                                                    {uploadedFiles.filter(file => file.match(/\.(jpeg|jpg|gif|png)$/)).map((file, index) => (
                                                                        <Box
                                                                            key={index}
                                                                            borderWidth="1px"
                                                                            borderColor={cardBorder}
                                                                            borderRadius="12px"
                                                                            overflow="hidden"
                                                                            boxShadow={`0 2px 4px -1px ${shadowColor}`}
                                                                        >
                                                                            <Image src={file} alt={`Imagen ${index + 1}`} />
                                                                        </Box>
                                                                    ))}
                                                                </SimpleGrid>
                                                            ) : (
                                                                <Box
                                                                    bg="cream.bg2"
                                                                    borderRadius="12px"
                                                                    p={6}
                                                                    textAlign="center"
                                                                >
                                                                    <Text color={mutedTextColor}>No hay imágenes subidas.</Text>
                                                                </Box>
                                                            )}
                                                        </Box>

                                                        {/* Archivos Subidos */}
                                                        <Box>
                                                            <Text fontSize="lg" fontWeight="800" color={textColor} mb={4}>
                                                                Otros Archivos
                                                            </Text>
                                                            {uploadedFiles?.filter(file => !file.match(/\.(jpeg|jpg|gif|png)$/)).length > 0 ? (
                                                                <Box
                                                                    bg="cream.bg2"
                                                                    borderRadius="12px"
                                                                    p={4}
                                                                >
                                                                    <List spacing={3}>
                                                                        {uploadedFiles.filter(file => !file.match(/\.(jpeg|jpg|gif|png)$/)).map((file, index) => {
                                                                            const fileName = file.split('/').pop();
                                                                            const fileExtension = fileName.split('.').pop();
                                                                            return (
                                                                                <ListItem key={index} display="flex" alignItems="center">
                                                                                    <Icon as={AttachmentIcon} color={mutedTextColor} mr={2} />
                                                                                    <Link href={file} isExternal color="sky.ink" fontWeight="700">
                                                                                        {fileName} ({fileExtension})
                                                                                    </Link>
                                                                                </ListItem>
                                                                            );
                                                                        })}
                                                                    </List>
                                                                </Box>
                                                            ) : (
                                                                <Box
                                                                    bg="cream.bg2"
                                                                    borderRadius="12px"
                                                                    p={6}
                                                                    textAlign="center"
                                                                >
                                                                    <Text color={mutedTextColor}>No hay otros archivos subidos.</Text>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </VStack>
                                                </TabPanel>
                                            )}
                                        </TabPanels>
                                    </Tabs>
                                </Box>
                            </VStack>
                        </HStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor={borderColor} justifyContent="space-between">
                        {/* Botón Eliminar - Lado izquierdo */}
                        <Tooltip label="Eliminar Pago Permanentemente">
                            <IconButton
                                icon={loadingStatusChange ? <Spinner size="sm" /> : <DeleteIcon />}
                                bg="transparent"
                                border="1px solid"
                                borderColor="danger.500"
                                color="danger.500"
                                _hover={{ bg: 'danger.bg' }}
                                aria-label="Eliminar Pago"
                                onClick={handleOrderDelete}
                                size="sm"
                            />
                        </Tooltip>

                        {/* Botones del lado derecho */}
                        <Box>
                            {/* Botón Comprobante - Solo para órdenes completadas */}
                            {updatedOrder?.status === 'completed' && (
                                <Button
                                    leftIcon={loadingVoucher ? <Spinner size="sm" /> : <FaReceipt />}
                                    bg="transparent"
                                    border="1px solid"
                                    borderColor="sky.ink"
                                    color="sky.ink"
                                    _hover={{ bg: 'sky.bg' }}
                                    onClick={handleGenerateVoucher}
                                    isLoading={loadingVoucher}
                                    loadingText="Generando..."
                                    mr={3}
                                >
                                    Comprobante
                                </Button>
                            )}

                            <Button onClick={onClose} variant="ghost" color="ink.400" _hover={{ bg: 'cream.bg2', color: 'ink.700' }}>
                                Cerrar
                            </Button>
                        </Box>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal de edición de usuario */}
            <Modal isOpen={editModalOpen} onClose={handleCancelEdit} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <form onSubmit={handleSubmit(handleUpdateCustomer)}>
                        <ModalHeader>Editar Usuario</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody pb={6}>
                            <FormControl isRequired>
                                <FormLabel>RUT</FormLabel>
                                <Input
                                    {...register("username", { required: "El RUT es requerido" })}
                                    onChange={(e) => e.target.value = format(e.target.value, { dots: false })}
                                    placeholder="Ejemplo: 12345678-9"
                                />
                            </FormControl>

                            <FormControl isRequired mt={4}>
                                <FormLabel>Email</FormLabel>
                                <Input
                                    type="email"
                                    {...register("email", { 
                                        required: "El email es requerido",
                                        pattern: { value: /^\S+@\S+$/i, message: "Email inválido" }
                                    })}
                                    placeholder="email@ejemplo.com"
                                />
                            </FormControl>

                            <FormControl isRequired mt={4}>
                                <FormLabel>Nombres</FormLabel>
                                <Input
                                    {...register("first_name", { required: "Los nombres son requeridos" })}
                                    placeholder="Nombres"
                                />
                            </FormControl>

                            <FormControl isRequired mt={4}>
                                <FormLabel>Apellidos</FormLabel>
                                <Input
                                    {...register("last_name", { required: "Los apellidos son requeridos" })}
                                    placeholder="Apellidos"
                                />
                            </FormControl>

                            <FormControl mt={4}>
                                <FormLabel>Grupo</FormLabel>
                                <Select {...register("group")} placeholder="Seleccione un grupo">
                                    <option value="">Sin grupo</option>
                                    {groups.map((grupo) => (
                                        <option key={grupo.name} value={grupo.name}>
                                            {grupo.name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            {moduleStatus[MODULES.SUBSCRIPTIONS] && (
                                <FormControl mt={4}>
                                    <FormLabel>Suscripción</FormLabel>
                                    <Select {...register("suscription")} placeholder="Seleccione una suscripción">
                                        <option value="">Sin suscripción</option>
                                        {products.map((product) => (
                                            <option key={product.id} value={product.name}>
                                                {product.name} - ${product.price}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                bg="coral.500"
                                color="white"
                                fontWeight="600"
                                borderRadius="10px"
                                _hover={{ bg: 'coral.600' }}
                                _active={{ bg: 'coral.700' }}
                                mr={3}
                                type="submit"
                                isLoading={editLoading}
                            >
                                Actualizar
                            </Button>
                            <Button variant="ghost" color="ink.400" _hover={{ bg: 'cream.bg2', color: 'ink.700' }} onClick={handleCancelEdit}>
                                Cancelar
                            </Button>
                        </ModalFooter>
                    </form>
                </ModalContent>
            </Modal>

            {/* Dialog de confirmación para email */}
            <AlertDialog
                isOpen={isEmailOpen}
                leastDestructiveRef={cancelRef}
                onClose={onEmailClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="800">
                            Enviar Email
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            ¿Estás seguro de que deseas enviar un email a {updatedOrder?.customer_info?.email}?
                            <br />
                            <Text mt={2} fontSize="sm" color={mutedTextColor}>
                                Se abrirá tu cliente de email predeterminado.
                            </Text>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onEmailClose} variant="ghost" color="ink.400" _hover={{ bg: 'cream.bg2', color: 'ink.700' }}>
                                Cancelar
                            </Button>
                            <Button
                                bg="coral.500"
                                color="white"
                                fontWeight="600"
                                borderRadius="10px"
                                _hover={{ bg: 'coral.600' }}
                                onClick={handleConfirmEmail}
                                ml={3}
                            >
                                Enviar Email
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Dialog de confirmación para teléfono */}
            <AlertDialog
                isOpen={isPhoneOpen}
                leastDestructiveRef={cancelRef}
                onClose={onPhoneClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="800">
                            Realizar Llamada
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            ¿Estás seguro de que deseas llamar al número{' '}
                            <Text as="span" fontWeight="700">
                                {updatedOrder?.customer_info?.meta_data?.find(m => m.key === 'telefono')?.value ||
                                 updatedOrder?.customer_info?.meta_data?.find(m => m.key === 'apoderado_telefono')?.value}
                            </Text>
                            ?
                            <br />
                            <Text mt={2} fontSize="sm" color={mutedTextColor}>
                                Se abrirá tu aplicación de teléfono predeterminada.
                            </Text>
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onPhoneClose} variant="ghost" color="ink.400" _hover={{ bg: 'cream.bg2', color: 'ink.700' }}>
                                Cancelar
                            </Button>
                            <Button
                                bg="ok.500"
                                color="white"
                                fontWeight="600"
                                borderRadius="10px"
                                _hover={{ opacity: 0.9 }}
                                onClick={handleConfirmPhone}
                                ml={3}
                            >
                                Llamar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default ModalOrderEdit;